import { useRef } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { useVirtualizer } from '@tanstack/react-virtual';

import type { IssueCard } from '@tasks/contracts';

import { IssueCardView } from '../issue/IssueCardView';
import styles from './BoardColumnView.module.css';

export type BoardColumnViewProps = {
  readonly issues: readonly IssueCard[];
  readonly onOpenIssue: (key: string) => void;
  readonly hasMore: boolean;
  readonly isLoadingMore: boolean;
  readonly onLoadMore: () => void;
};

const CARD_ESTIMATE_PX = 100;
const VIRTUALIZE_THRESHOLD = 120;

/**
 * Карточка-источник перетаскивания. Отдельный компонент нужен, потому что
 * `useDraggable` — хук: его нельзя вызвать в цикле по списку.
 *
 * `role: 'article'` вместо дефолтного `button` из @dnd-kit: внутри карточки есть
 * собственные кнопки, а вложенные интерактивные роли — нарушение a11y.
 * `transform` намеренно не применяется: источник остаётся на месте и лишь
 * притухает, за курсором следует `<DragOverlay>` (docs/06 §3.1).
 */
function BoardCard({ issue, onOpenIssue }: { readonly issue: IssueCard; readonly onOpenIssue: (key: string) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `issue:${issue.id}`,
    data: { card: issue },
    attributes: { role: 'article' },
  });

  // `Space`/`Enter` берут карточку (docs/06 §3.1), но только когда фокус на ней
  // самой: иначе клавиатурный захват перехватывал бы активацию кнопок внутри
  // карточки — «Открыть задачу» и заголовок.
  const dragListeners = listeners ?? {};
  const baseKeyDown = dragListeners['onKeyDown'];

  const dragProps: Record<string, unknown> = {
    ...attributes,
    ...dragListeners,
    onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
      if (event.target !== event.currentTarget) {
        return;
      }
      baseKeyDown?.(event);
    },
  };

  return (
    <IssueCardView
      issue={issue}
      onOpen={onOpenIssue}
      isDragging={isDragging}
      dragNodeRef={setNodeRef}
      dragProps={dragProps}
    />
  );
}

/** Виртуализация колонки с подгрузкой по 50 (UC-BRD-01 AC3). */
export function BoardVirtualList({
  issues,
  onOpenIssue,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: BoardColumnViewProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const shouldVirtualize = issues.length > VIRTUALIZE_THRESHOLD;

  const virtualizer = useVirtualizer({
    count: issues.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => CARD_ESTIMATE_PX,
    overscan: 6,
    enabled: shouldVirtualize,
  });

  const handleScroll = (): void => {
    const node = scrollRef.current;
    if (!node || !hasMore || isLoadingMore) {
      return;
    }
    if (node.scrollTop + node.clientHeight >= node.scrollHeight - 120) {
      onLoadMore();
    }
  };

  if (!shouldVirtualize) {
    return (
      <div className={styles['list']} ref={scrollRef} onScroll={handleScroll} data-testid="column-list">
        {issues.map((issue) => (
          <div key={issue.id} className={styles['item']}>
            <BoardCard issue={issue} onOpenIssue={onOpenIssue} />
          </div>
        ))}
      </div>
    );
  }

  const items = virtualizer.getVirtualItems();
  return (
    <div className={styles['list']} ref={scrollRef} onScroll={handleScroll} data-testid="column-list">
      <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
        {items.map((item) => {
          const issue = issues[item.index];
          if (!issue) {
            return null;
          }
          return (
            <div
              key={issue.id}
              className={styles['item']}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                transform: `translateY(${item.start}px)`,
              }}
              ref={virtualizer.measureElement}
              data-index={item.index}
            >
              <BoardCard issue={issue} onOpenIssue={onOpenIssue} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
